// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { cn } from '@/lib/utils';

export interface SegmentedControlProps<T extends string> {
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  'aria-label'?: string;
  className?: string;
}

// Generic component with explicit return type annotation on the overload signature.
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  disabled = false,
  'aria-label': ariaLabel,
  className,
}: SegmentedControlProps<T>): React.ReactElement {
  const buttonRefs = React.useRef<Map<T, HTMLButtonElement>>(new Map());

  // Roving tabIndex: only the selected radio has tabIndex=0; others are -1.
  // Arrow keys move selection and focus between options (ARIA radiogroup pattern).
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, idx: number) => {
    if (disabled) return;
    let next = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (idx + 1) % options.length;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp')
      next = (idx - 1 + options.length) % options.length;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = options.length - 1;
    if (next === -1) return;
    e.preventDefault();
    const nextOption = options[next];
    if (!nextOption) return;
    onChange(nextOption.value);
    buttonRefs.current.get(nextOption.value)?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn('inline-flex rounded-gl border border-border bg-muted p-0.5', className)}
    >
      {options.map((option, idx) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            disabled={disabled}
            tabIndex={isActive ? 0 : -1}
            ref={(el) => {
              if (el) buttonRefs.current.set(option.value, el);
              else buttonRefs.current.delete(option.value);
            }}
            onClick={() => onChange(option.value)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            className={cn(
              'h-11 rounded-[5px] px-3.5 text-[13px] font-medium transition-colors md:h-8',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              'disabled:pointer-events-none disabled:opacity-50',
              isActive
                ? 'bg-card font-semibold text-foreground shadow-sm ring-1 ring-inset ring-input'
                : 'text-foreground',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
